import { selMessage } from "@selector/shared";

import { createErrorHandler } from "@/app-utils/errors.ts";
import { type InputUserAccountInput, type User } from "@/graphql.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { notify } from "../../modules/notifications/notify.js";
import {
  CHECK_ACCEPTABLE_NEW_USERNAME_FAILURE,
  CREATE_USERS_ADMIN_FAILURE,
  CREATE_USERS_ADMIN_REQUEST,
  CREATE_USERS_ADMIN_SUCCESS,
  GET_NUMBER_OF_FREE_SEATS_FAILURE,
  GET_NUMBER_OF_FREE_SEATS_SUCCESS,
  INVALIDATE_PASSWORD,
  LOAD_POTENTIAL_LDAP_USERS_ADMIN_FAILURE,
  LOAD_POTENTIAL_LDAP_USERS_ADMIN_REQUEST,
  LOAD_POTENTIAL_LDAP_USERS_ADMIN_SUCCESS,
  LOAD_USERS_ADMIN_FAILURE,
  LOAD_USERS_ADMIN_REQUEST,
  LOAD_USERS_ADMIN_SUCCESS,
  MODIFY_USER_ADMIN_FAILURE,
  MODIFY_USER_ADMIN_REQUEST,
  MODIFY_USER_ADMIN_SUCCESS,
} from "../action-types.js";
import { createReducer, type ReduxAction } from "../redux-utils.js";
import { loadUserCapabilities, loadUserSession } from "./auth.js";

export const AUTHENTICATION_MODE = {
  ORACLE: "oracle",
  LDAP: "ldap",
};

/**
 * State definition
 */

export interface IUsersAdminState {
  users: User[];
  isLoadingUsers: boolean;

  potentialLdapUsers: User[];

  numberOfFreeSeats: number;
  canCreateOracleUser: boolean;
  authenticationMode: string;
}

const initialState: IUsersAdminState = {
  users: [],
  potentialLdapUsers: [],
  isLoadingUsers: false,

  numberOfFreeSeats: 0,
  canCreateOracleUser: false,
  authenticationMode: null,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_USERS_ADMIN_REQUEST]: (state: IUsersAdminState): IUsersAdminState => {
    return { ...state, isLoadingUsers: true };
  },

  [LOAD_USERS_ADMIN_SUCCESS]: (state: IUsersAdminState, users: User[]): IUsersAdminState => {
    return { ...state, users, isLoadingUsers: false };
  },

  [LOAD_USERS_ADMIN_FAILURE]: (state: IUsersAdminState): IUsersAdminState => {
    return { ...state, isLoadingUsers: false };
  },

  [GET_NUMBER_OF_FREE_SEATS_SUCCESS]: (state: IUsersAdminState, numberOfFreeSeats: number): IUsersAdminState => {
    return { ...state, numberOfFreeSeats: numberOfFreeSeats };
  },

  [LOAD_POTENTIAL_LDAP_USERS_ADMIN_SUCCESS]: (state: IUsersAdminState, users: User[]): IUsersAdminState => {
    return { ...state, users, potentialLdapUsers: users };
  },
});

/**
 * Action creators
 */

export function loadAllSessions() {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({ type: LOAD_USERS_ADMIN_REQUEST });

    return webApiClient
      .fetchUsersForAdmin()
      .then((sessions: User[]) => {
        dispatch({
          type: LOAD_USERS_ADMIN_SUCCESS,
          payload: sessions,
        });
        return sessions;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_USERS_ADMIN_FAILURE,
          alertTitle: "Fetching of Users Failed",
        }),
      );
  };
}

export function loadPotentialLdapUsers() {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({ type: LOAD_POTENTIAL_LDAP_USERS_ADMIN_REQUEST });

    return webApiClient
      .fetchPossibleLdapUsersForAdmin()
      .then((users: User[]) => {
        dispatch({
          type: LOAD_POTENTIAL_LDAP_USERS_ADMIN_SUCCESS,
          payload: users,
        });
        return users;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_POTENTIAL_LDAP_USERS_ADMIN_FAILURE,
          alertTitle: "Fetching of Potential LDAP Users Failed",
        }),
      );
  };
}

export function addUser(user: InputUserAccountInput) {
  return (dispatch, _getState, _webApiClient: WebApiClient) => {
    return dispatch(createUsers([user])).then((sessions) => {
      notify.info({
        title: selMessage({ id: "admin.addUser.successTitle" }),
        children: selMessage({
          id: "admin.addUser.successMessage",
          values: { firstName: user.firstName, lastName: user.lastName },
        }),
      });
      return sessions;
    });
  };
}

export function addUsers(users: InputUserAccountInput[]) {
  return (dispatch, _getState, _webApiClient: WebApiClient) => {
    return dispatch(createUsers(users)).then((sessions) => {
      notify.info(
        {
          title: selMessage({ id: "admin.addUsers.successTitle", values: { userCount: users.length } }),
          children: selMessage({ id: "admin.addUsers.successMessage", values: { userCount: users.length } }),
        },
        { autoDismiss: 5 },
      );
      return sessions;
    });
  };
}

function createUsers(users: InputUserAccountInput[]) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({ type: CREATE_USERS_ADMIN_REQUEST });

    return webApiClient
      .createUsers(users)
      .then((sessions: any) => {
        dispatch({
          type: CREATE_USERS_ADMIN_SUCCESS,
          payload: sessions,
        });
        dispatch(fetchNumberOfFreeSeats());
        return sessions;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: CREATE_USERS_ADMIN_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function modifyUser(user: InputUserAccountInput) {
  return (dispatch, getState, webApiClient: WebApiClient) => {
    dispatch({ type: MODIFY_USER_ADMIN_REQUEST });

    return webApiClient
      .modifyUser(user)
      .then((response: any) => {
        dispatch({
          type: MODIFY_USER_ADMIN_SUCCESS,
          payload: response,
        });
        if (user.username === getState().auth.session.username) {
          dispatch(loadUserCapabilities());
        }
        // refresh the user data because they contain usergroups of the current user,
        // reflecting changes in the active usergroup members (if <user> was enabled/disabled)
        dispatch(loadUserSession());
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: MODIFY_USER_ADMIN_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function acceptableNewUserName(username: string) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    return webApiClient.validateIsAcceptableNewOracleAccountNameQueryOptions(username).catch(
      createErrorHandler(dispatch, {
        actionType: CHECK_ACCEPTABLE_NEW_USERNAME_FAILURE,
        showAlert: false,
        throwError: true,
      }),
    );
  };
}

export function fetchNumberOfFreeSeats() {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    return webApiClient
      .fetchNumberOfFreeSeats()
      .then((numberOfFreeSeats: number) => {
        dispatch({
          type: GET_NUMBER_OF_FREE_SEATS_SUCCESS,
          payload: numberOfFreeSeats,
        });
        return numberOfFreeSeats;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: GET_NUMBER_OF_FREE_SEATS_FAILURE,
          alertTitle: "Could Not Fetch the Number of Free Seats Left",
        }),
      );
  };
}

export function invalidatePasswords(userIds: number[]): ReduxAction<Promise<boolean>> {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<boolean> => {
    const users = getState().usersAdmin.users;
    const usernames: string[] = userIds
      .map((userId: number) => users.find((user: User) => user.id === userId))
      .filter((user: User) => user)
      .map((user: User) => user.username);

    return webApiClient.invalidatePasswords(usernames).catch(
      createErrorHandler(dispatch, {
        actionType: INVALIDATE_PASSWORD,
        showAlert: false,
        throwError: true,
      }),
    );
  };
}

/************** Utils ***********/

export function noMoreFreeSeatsText() {
  return "There are no seats available in the license for additional users. Please remove users or contact Genedata for license support.";
}

export function noMoreFreeSeatsHint() {
  return "No available seats";
}

export function noEnoughFreeSeatsHint() {
  return "Not enough available seats";
}

export function noEnoughFreeSeatsText(numberOfFreeSeats: number) {
  return `There are only ${numberOfFreeSeats} seats available in the license for additional users. Please remove users or contact Genedata for license support.`;
}
